import React, { useMemo } from 'react';
import { buildPaletteTheme, getCampaignPalettesShifted } from '../../themes/palettes';
import { TopBar } from '../chrome/TopBar';
import { SectionHeader } from '../primitives/SectionHeader';
import { PalettePageGradient } from '../primitives/PalettePageGradient';
import { DemoButton } from '../primitives/buttons';
import { PartyJournalIcon } from '../primitives/icons';
import { TableNote } from '../home/TableNote';
import { GetStartedRow } from '../home/GetStartedRow';
import { CampaignHeroStrip } from './CampaignHeroStrip';
import { CharacterRow } from './CharacterRow';
import { CharacterEmptyState } from './CharacterEmptyState';
import { NextSessionCard } from './NextSessionCard';
import { PresenceList } from './PresenceList';
import { getEffectiveCampaignFeatures } from '../../../../campaignFeatureAccess';
import { isCampaignTestCharacter } from '../../../../campaignCharacterTargets';

export const CampaignView = ({
  theme, density, palettes,
  cardStyle, cardFinish,
  bgColor, setBgColor, themeBgColor,
  onOpenThemes,
  campaignId, onBack,
  data,
  actions,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const e = theme.effects || {};

  const user = data?.user || { name: '', handle: '@you', initial: '?' };
  const campaigns = data?.campaigns || [];
  const campaign = campaigns.find(cp => cp.id === campaignId);
  // Hooks must run on every render — derive nullable inputs and decide
  // whether to render after all hooks have been called.
  const hueShift = campaign?.hueShift || 0;

  // Generate this campaign's own 5-slot palette set, rotated by its
  // hueShift around the color wheel. Each campaign therefore owns a
  // distinct hue region — Lockhood at 0°, Thunder King at 120°, Abyss
  // at 240° — instead of all three sharing the theme's accent family.
  const campaignPalettes = useMemo(
    () => getCampaignPalettesShifted(theme, hueShift),
    [theme, hueShift]
  );

  // Within this campaign's hue-shifted palette set, use slot 0 as the
  // primary, then rotate +1/+2/+3 across the four secondary surfaces.
  const palette = campaignPalettes[0];
  const slotCount = campaignPalettes.length;
  const toolsPalette   = campaignPalettes[1 % slotCount];
  const accentPalette  = campaignPalettes[2 % slotCount];
  const supportPalette = campaignPalettes[3 % slotCount];
  const palTheme = useMemo(
    () => buildPaletteTheme(theme, palette, cardStyle, cardFinish),
    [theme, palette, cardStyle, cardFinish]
  );

  if (!campaign) {
    return (
      <>
        <PalettePageGradient palette={palette} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <TopBar
            theme={theme}
            density={density}
            user={user}
            onOpenThemes={onOpenThemes}
            bgColor={bgColor}
            setBgColor={setBgColor}
            themeBgColor={themeBgColor}
          />
          <button
            onClick={onBack}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'transparent',
              border: `1px solid ${c.borderLight}`,
              color: c.textSecondary,
              padding: '6px 12px',
              borderRadius: e.borderRadiusSm || '6px',
              fontFamily: t.fontFamily,
              fontSize: '0.82rem',
              cursor: 'pointer',
              marginBottom: 14,
            }}
          >
            <span style={{ fontSize: '0.92rem' }}>{'<-'}</span> Back to home
          </button>
          <div
            role="status"
            style={{
              padding: density.spacing.cardPadding,
              border: `1px solid ${c.borderLight}`,
              borderRadius: e.borderRadius || '8px',
              background: c.bgCardSolid,
              color: c.textSecondary,
              fontFamily: t.fontFamily,
              maxWidth: 560,
            }}
          >
            Campaign is syncing. If it does not appear in a moment, return home and reopen it.
          </div>
        </div>
      </>
    );
  }

  const isDemoCampaign = !!campaign.isDemo;
  const canManageCampaign = !isDemoCampaign && (campaign?.role === 'owner' || campaign?.role === 'dm');
  const effectiveFeatures = campaign?.effectiveFeatures || getEffectiveCampaignFeatures(campaign?.enabledFeatures || null);
  const chars = data?.campaignCharacters?.[campaignId] || [];
  const friends = data?.campaignFriends?.[campaignId] || [];
  const showTestToggle = canManageCampaign && (campaign.totalTestCharacterCount || 0) > 0;
  const extra = data?.campaignExtra?.[campaignId] || {};
  // Per-campaign announcements come from listForCampaign via the data hook
  // (Phase C); falls back to filtering the cross-campaign tableNotes in mock
  // mode so the prototype demo still works.
  const campaignNotes = data?.announcementsForCampaign?.[campaignId]
    || (data?.tableNotes || []).filter(n => n.campaignId === campaignId);
  const onlineCount = friends.filter(f => f.status !== 'offline').length;

  // Section panels: same open-region treatment as the home view.
  const sectionRegionStyle = {
    paddingTop: density.spacing.sectionGap,
    paddingBottom: density.spacing.sectionGap,
    marginBottom: density.spacing.cardGap,
    borderTop: `1px solid ${c.borderLight}`,
  };
  const firstSectionRegionStyle = { ...sectionRegionStyle, borderTop: 'none', paddingTop: 0 };
  const characterHeaderAction = showTestToggle || (!isDemoCampaign && chars.length > 0) ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {showTestToggle && (
        <button
          type="button"
          onClick={() => actions?.onSetShowTestCharacters?.(campaignId, !campaign.showTestCharacters)}
          style={{
            background: 'transparent',
            border: `1px solid ${palette.color}66`,
            color: palette.light,
            fontSize: '0.74rem',
            cursor: 'pointer',
            padding: '4px 10px',
            borderRadius: 4,
            fontFamily: t.fontFamily,
          }}
        >
          {campaign.showTestCharacters ? 'Hide saved practice' : 'Show saved practice'}
        </button>
      )}
      {!isDemoCampaign && chars.length > 0 && (
        <DemoButton
          theme={palTheme}
          icon="*"
          variant="outline"
          onClick={() => actions?.onOpenCampaignCharacterSetup?.(campaignId)}
        >
          Add character
        </DemoButton>
      )}
    </div>
  ) : null;

  return (
    <>
      <PalettePageGradient palette={palette} />

      <div style={{ position: 'relative', zIndex: 1 }}>
      <TopBar
        theme={theme}
        density={density}
        user={user}
        onOpenThemes={onOpenThemes}
        bgColor={bgColor}
        setBgColor={setBgColor}
        themeBgColor={themeBgColor}

      />

      {/* Breadcrumb */}
      <button
        onClick={onBack}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent',
          border: `1px solid ${c.borderLight}`,
          color: c.textSecondary,
          padding: '6px 12px',
          borderRadius: e.borderRadiusSm || '6px',
          fontFamily: t.fontFamily,
          fontSize: '0.82rem',
          cursor: 'pointer',
          marginBottom: 14,
        }}
      >
        <span style={{ fontSize: '0.92rem' }}>←</span> Back to home
      </button>

      {/* Campaign hero strip — same construction as the home view's active
          character card but parameterized by the campaign palette. */}
      <CampaignHeroStrip
        theme={theme} density={density}
        campaign={campaign}
        extra={extra}
        palette={palette}
        cardStyle={cardStyle}
        cardFinish={cardFinish}
        onEnterAsDm={() => actions?.onEnterAsCampaignDm?.(campaignId)}
        onOpenSettings={isDemoCampaign ? undefined : () => actions?.onOpenCampaignSettings?.(campaignId)}
        onCreateInvite={canManageCampaign ? () => actions?.onCreateCampaignInvite?.(campaignId) : undefined}
      />

      {/* Two-column body */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
        gap: density.spacing.cardGap,
        alignItems: 'flex-start',
      }}>
        {/* LEFT */}
        <div>
          <div style={firstSectionRegionStyle}>
            <SectionHeader
              icon="✦"
              title="My Characters"
              count={chars.length}
              theme={theme} density={density}
              action={characterHeaderAction}
            />
            {chars.length === 0 ? (
              <CharacterEmptyState
                theme={theme} density={density}
                palette={palette} palTheme={palTheme}
                cardStyle={cardStyle} cardFinish={cardFinish}
                onAddCharacter={() => actions?.onOpenCampaignCharacterSetup?.(campaignId)}
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: density.spacing.itemGap }}>
                {chars.map((ch) => {
                  const isSavedPractice = isCampaignTestCharacter(ch);
                  const characterId = ch.charId || ch.id;
                  return (
                    <CharacterRow
                      key={ch.id} theme={theme} density={density}
                      ch={ch} palette={palette}
                      cardStyle={cardStyle} cardFinish={cardFinish}
                      onPlay={ch.isPlayable === false || isSavedPractice
                        ? undefined
                        : () => actions?.onEnterAsCampaignChar?.(campaignId, characterId)}
                      onRemove={canManageCampaign
                        ? () => actions?.onRemoveCampaignCharacter?.(campaignId, characterId, ch.name)
                        : undefined}
                    />
                  );
                })}
              </div>
            )}
          </div>

          <div style={sectionRegionStyle}>
            <SectionHeader
              icon="✦"
              title="Announcements"
              count={campaignNotes.length}
              theme={theme} density={density}
              action={canManageCampaign ? (
                <button
                  type="button"
                  onClick={() => actions?.onOpenAnnouncementComposer?.(campaignId, null)}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${palette.color}66`,
                    color: palette.light,
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    padding: '4px 10px',
                    borderRadius: 4,
                    fontFamily: t.fontFamily,
                  }}
                >+ Compose</button>
              ) : undefined}
            />
            {campaignNotes.length === 0 ? (
              <div style={{
                fontSize: '0.82rem', color: c.textMuted, padding: '12px 4px',
                fontFamily: t.fontFamily, fontStyle: 'italic',
              }}>
                {canManageCampaign
                  ? "Nothing posted yet. Use Compose to share something with the table."
                  : "Your DM hasn't posted anything yet."}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: density.spacing.itemGap }}>
                {campaignNotes.map(n => {
                  const canManage = canManageCampaign;
                  return (
                    <div key={n.id} style={{ position: 'relative' }}>
                      <TableNote
                        theme={theme} density={density}
                        note={{ ...n, campaignName: campaign.name, palette }}
                        palette={palette}
                      />
                      {canManage && (
                        <button
                          type="button"
                          onClick={() => actions?.onOpenAnnouncementComposer?.(campaignId, n)}
                          title="Edit this announcement"
                          style={{
                            position: 'absolute',
                            top: 8, right: 8,
                            background: 'rgba(0,0,0,0.55)',
                            border: `1px solid ${palette.color}66`,
                            color: palette.light,
                            borderRadius: 4,
                            padding: '2px 8px',
                            fontSize: '0.74rem',
                            cursor: 'pointer',
                            fontFamily: t.fontFamily,
                          }}
                        >Edit</button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT */}
        <div>
          <div style={firstSectionRegionStyle}>
            <SectionHeader icon="✦" title="Next Session" theme={theme} density={density} />
            <NextSessionCard
              theme={theme} density={density}
              schedule={campaign.schedule}
              palette={accentPalette} cardStyle={cardStyle} cardFinish={cardFinish}
            />
          </div>

          {friends.length > 0 && (
            <div style={sectionRegionStyle}>
              <SectionHeader
                icon="✦" title="Around the Table"
                theme={theme} density={density}
                action={
                  <span style={{
                    fontSize: '0.7rem', color: c.textMuted,
                    letterSpacing: '0.18em',
                    fontFamily: t.fontFamily,
                  }}>{onlineCount} OF {friends.length} ONLINE</span>
                }
              />
              <PresenceList theme={theme} density={density} friends={friends} palette={supportPalette} cardStyle={cardStyle} cardFinish={cardFinish} />
            </div>
          )}

          {canManageCampaign && (
            <div style={sectionRegionStyle}>
              <SectionHeader icon="*" title="Sandbox" theme={theme} density={density} />
              <GetStartedRow
                theme={theme} density={density}
                icon="*"
                title="Open / resume sandbox"
                body="local practice campaign"
                palette={toolsPalette}
                onClick={() => actions?.onOpenCampaignSandbox?.(campaignId)}
              />
              <GetStartedRow
                theme={theme} density={density}
                icon="*"
                title="Reset and reseed sandbox"
                body="replace local sandbox seed data"
                palette={accentPalette}
                onClick={() => actions?.onResetCampaignSandbox?.(campaignId)}
              />
              <GetStartedRow
                theme={theme} density={density}
                icon="*"
                title="Sync campaign to sandbox"
                body="refresh sandbox from current campaign setup"
                palette={supportPalette}
                onClick={() => actions?.onSyncCampaignSandbox?.(campaignId)}
              />
            </div>
          )}

          {!isDemoCampaign && (
            <div style={sectionRegionStyle}>
              <SectionHeader icon="✦" title="Campaign Tools" theme={theme} density={density} />
              <GetStartedRow
                theme={theme} density={density}
                icon={<PartyJournalIcon size={34} />}
                title="Party Journal"
                body="past sessions and notes"
                palette={toolsPalette}
                onClick={() => actions?.onOpenPartyJournal?.(campaignId)}
              />
              <GetStartedRow
                theme={theme} density={density}
                icon={<PartyJournalIcon size={34} />}
                title="World Lore / Atlas"
                body="revealed lore and places"
                palette={supportPalette}
                onClick={() => actions?.onOpenWorldLore?.(campaignId)}
              />
              {effectiveFeatures.travelLog && (
                <GetStartedRow
                  theme={theme} density={density}
                  icon="📜"
                  title="Travel Log"
                  body="days, meals, and travel history"
                  palette={accentPalette}
                  onClick={() => actions?.onOpenCampaignModule?.(campaignId, 'travel')}
                />
              )}
              {effectiveFeatures.combat && (
                <GetStartedRow
                  theme={theme} density={density}
                  icon="⚔"
                  title="Combat"
                  body="turns, health bars, spells, and abilities"
                  palette={toolsPalette}
                  onClick={() => actions?.onOpenCampaignModule?.(campaignId, 'combat')}
                />
              )}
              {effectiveFeatures.shops && (
                <GetStartedRow
                  theme={theme} density={density}
                  icon="🏪"
                  title="Shops"
                  body="merchants and campaign wallets"
                  palette={supportPalette}
                  onClick={() => actions?.onOpenCampaignModule?.(campaignId, 'shop')}
                />
              )}
              {effectiveFeatures.loot && (
                <GetStartedRow
                  theme={theme} density={density}
                  icon="⚜"
                  title="Loot"
                  body="encounter drops and awards"
                  palette={accentPalette}
                  onClick={() => actions?.onOpenCampaignModule?.(campaignId, 'loot')}
                />
              )}
              {effectiveFeatures.crafting && (
                <GetStartedRow
                  theme={theme} density={density}
                  icon="⚒"
                  title="Crafting"
                  body={effectiveFeatures.professions ? 'recipes, materials, and professions' : 'recipes and materials'}
                  palette={toolsPalette}
                  onClick={() => actions?.onOpenCampaignModule?.(campaignId, 'crafting')}
                />
              )}
              {canManageCampaign && effectiveFeatures.systems && (
                <GetStartedRow
                  theme={theme} density={density}
                  icon="🔮"
                  title="Systems"
                  body="custom campaign mechanics"
                  palette={supportPalette}
                  onClick={() => actions?.onOpenCampaignModule?.(campaignId, 'dm')}
                />
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </>
  );
};
